import { THEME_COLORS } from '@st/theme/colors'
import { Rect } from '@st/geom'
import { StampTemplate, TextStampTemplateSection } from '~/st-doc-viewer/annotations'
import { recomputeStampLayoutAndStyle } from './stamp-layout'
import { StampAnnotation } from '@st/sdk'

const RED = THEME_COLORS['red'][600]
const BLACK = '#000000'
const WHITE = '#ffffff'
const YELLOW = THEME_COLORS['yellow'][300]

const ZERO_RECT: Rect = {
  x: 0,
  y: 0,
  width: 0,
  height: 0
}

type TextStamp = {
  text: string
  label: string
}

type NumberStamp = {
  text: string
  label: string
}

type ReferenceStamp = { text: string; label: string; stampCode: string }

/**
 * A small number of frequently used stamps (for now only the checkmark ✓)
 * are not bundled into the stamp tool dropdown.
 *
 * Instead, because they are their own buttons apart from the stamp selector.
 * This function lets us tell them apart so we can adapt the UI based on what is selected.
 *
 * For example, we do not show the dropdown when a checkmark is selected because there is
 * a dedicated buttton for those.
 */
export function isStandaloneStamp(annotation: StampAnnotation | undefined, text?: string) {
  if (!annotation) {
    return false
  }
  if (annotation.stampType != 'tick_mark') {
    return false
  }
  const candidateStampTemplates = [CHECKMARK_TICK_STAMP]
  if (text) {
    candidateStampTemplates.filter((template) => template.annotation.text == text)
  }
  return candidateStampTemplates.some((stamp) => stamp.annotation.text == annotation.text)
}

export const CHECKMARK_TICK_STAMP: StampTemplate = {
  label: '✓',
  category: 'checkmark',
  annotation: recomputeStampLayoutAndStyle({
    id: '✓',
    text: '✓',
    time: '2000-01-01T00:00:00.000Z',
    stampType: 'tick_mark',
    stampSize: 20,
    color: RED,
    bounds: ZERO_RECT,
    type: 'stamp',
    pageNumber: 1,
    font: {
      family: 'Helvetica',
      size: -1,
      weight: 'normal'
    }
  })
}

const STANDALONE_STAMPS: StampTemplate[] = [CHECKMARK_TICK_STAMP]

const TEXT_STAMPS: TextStamp[] = [
  {
    text: 'DUP',
    label: 'Duplicate'
  },
  {
    text: 'DUP!',
    label: 'Duplicate - Conflicting'
  },
  {
    text: 'NTE',
    label: 'No Tax Effect'
  },
  {
    text: 'T',
    label: 'Totaled'
  },
  {
    text: 'TR',
    label: 'Tax Return'
  },
  {
    text: 'PBC',
    label: 'Prepared by Client'
  },
  {
    text: 'R',
    label: 'Reviewed'
  },
  {
    text: 'V',
    label: 'Verified'
  },
  {
    text: '?',
    label: 'Unclear'
  }
]

const NUMBER_STAMPS: NumberStamp[] = [
  {
    text: '1',
    label: 'One'
  },
  {
    text: '2',
    label: 'Two'
  },
  {
    text: '3',
    label: 'Three'
  },
  {
    text: '4',
    label: 'Four'
  },
  {
    text: '5',
    label: 'Five'
  }
]

export const TICK_MARK_STAMP_TEMPLATE_SECTIONS: TextStampTemplateSection[] = [
  {
    title: 'Stamps',
    layout: 'rows',
    templates: TEXT_STAMPS.map((stamp) => ({
      label: stamp.label,
      category: 'tick_mark',
      annotation: recomputeStampLayoutAndStyle({
        id: stamp.text,
        text: stamp.text,
        time: '2000-01-01T00:00:00.000Z',
        stampType: 'tick_mark',
        stampSize: 16,
        color: RED,
        bounds: ZERO_RECT,
        type: 'stamp',
        pageNumber: 1,
        font: {
          family: 'Helvetica',
          size: -1,
          weight: 'normal'
        }
      })
    }))
  },
  {
    title: 'Numbers',
    layout: 'grid',
    templates: NUMBER_STAMPS.map((stamp) => ({
      label: stamp.label,
      category: 'tick_mark',
      annotation: recomputeStampLayoutAndStyle({
        id: stamp.text,
        text: stamp.text,
        time: '2000-01-01T00:00:00.000Z',
        stampType: 'number',
        stampSize: 20,
        color: BLACK,
        backgroundColor: WHITE,
        bounds: ZERO_RECT,
        border: {
          color: RED,
          width: 2,
          radius: 10
        },
        type: 'stamp',
        pageNumber: 1,
        font: {
          family: 'Helvetica',
          size: -1,
          weight: 'normal'
        }
      })
    }))
  }
]

const REFERENCES: ReferenceStamp[] = [
  { text: 'R-1', stampCode: 'R-1', label: 'Reference 1' },
  { text: 'R-2', stampCode: 'R-2', label: 'Reference 2' },
  { text: 'R-3', stampCode: 'R-3', label: 'Reference 3' },
  { text: 'R-4', stampCode: 'R-4', label: 'Reference 4' },
  { text: 'R-5', stampCode: 'R-5', label: 'Reference 5' },
  { text: 'R-6', stampCode: 'R-6', label: 'Reference 6' }
]

export const REFERENCE_STAMP_TEMPLATE_SECTIONS: TextStampTemplateSection[] = [
  {
    title: 'References',
    layout: 'rows',
    templates: REFERENCES.map(({ text, stampCode, label }) => ({
      label: label,
      category: 'cross_reference',
      annotation: recomputeStampLayoutAndStyle({
        id: text,
        text: text,
        time: '2000-01-01T00:00:00.000Z',
        stampType: 'cross_reference',
        stampCode: stampCode,
        stampSize: 20,
        color: RED,
        backgroundColor: YELLOW,
        bounds: ZERO_RECT,
        type: 'stamp',
        pageNumber: 1,
        font: {
          family: 'Helvetica',
          size: -1,
          weight: 'normal'
        }
      })
    }))
  }
]

export function findMatchingTemplate(
  annotation: StampAnnotation | undefined
): StampTemplate | undefined {
  if (!annotation) {
    return undefined
  }

  for (const template of STANDALONE_STAMPS) {
    if (templateMatches(template, annotation)) {
      return template
    }
  }

  for (const section of TICK_MARK_STAMP_TEMPLATE_SECTIONS) {
    for (const template of section.templates) {
      if (templateMatches(template, annotation)) {
        return template
      }
    }
  }

  for (const section of REFERENCE_STAMP_TEMPLATE_SECTIONS) {
    for (const template of section.templates) {
      if (templateMatches(template, annotation)) {
        return template
      }
    }
  }

  function templateMatches(template: StampTemplate, annotation: StampAnnotation) {
    return (
      template.annotation.text == annotation.text &&
      template.annotation.stampType == annotation.stampType
    )
  }
}
